import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import BookCard from './BookCard/inex';
import BookCardSkeleton from './BookCardSkeleton';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import Heading from '../../shared/Heading';
import CustomLoader from '../../shared/Loading';
import { notify } from '../../shared/notify';
import { getAllProducts } from '../../../services/Products';
import { PaginationProps } from '../../../utils/interfaces/Orders';
import { BookItems } from '../../../utils/interfaces/Products/books';

interface Props {
  heading?: string;
  isPagination?: boolean;
}

const BooksSection: React.FC<Props> = ({ isPagination = false }) => {
  const [loading, setLoading] = useState(false);
  const [bookData, setBookData] = useState<BookItems[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationProps>();
  const [page, setPage] = useState<number>(1);

  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    triggerOnce: false,
  });

  const environment = process.env.NEXT_PUBLIC_APP_ENV || '';

  const handleBookDetails = async () => {
    if (loading) return;

    try {
      setLoading(true);

      const data = await getAllProducts(
        page,
        6,
        'hardcover',
        undefined,
        page === 1 ? 8 : 6,
        false,
      );
      const formattedData = formatBookData(data?.data);

      setPaginationData({
        current_page: data?.meta?.current_page,
        per_page: data?.meta?.per_page,
        total: data?.meta?.total,
      });

      setBookData((prevBookData) => {
        const uniqueData = prevBookData.filter(
          (prevItem) =>
            !formattedData.some(
              (newItem) => newItem.hardCopyId === prevItem.hardCopyId,
            ),
        );
        return [...uniqueData, ...formattedData];
      });
    } catch (error: any) {
      notify('Failed to load books. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatBookData = (rawData: any[]): BookItems[] => {
    return rawData.map((book) => ({
      hardCopyId: book?.id || '',
      softCopyId: book?.digital_pair?.id || '',
      bookImage: book?.thumbnail_image || '',
      actualPriceHardCopy: book?.stroked_price || 0,
      softCopyHeading: book?.digital_pair?.name || 0,
      slug: book?.slug || '',
      actualPriceSoftCopy:
        (book?.digital_pair && book?.digital_pair?.stroked_price) || 0,
      heading: book?.name || '',
      authorName: book?.author?.name || '',
      desc: book?.description ?? '',
      hardCopyPrice: book?.main_price || 0,
      softCopyPrice:
        (book?.digital_pair && book?.digital_pair?.main_price) || 0,
      authorImage: book?.author?.image_url || '',
      currency: book?.currency_symbol || '',
      type: book?.category || '',
      external_link: book?.external_link || '',
      is_available_soft_copy: book?.digital_pair
        ? book?.digital_pair?.is_available &&
          book?.digital_pair?.i18n_attributes
        : null,
      is_available_hard_copy: book?.is_available && book?.i18n_attributes,
      i18n_attributes: book?.i18n_attributes || {},
    }));
  };

  useEffect(() => {
    handleBookDetails();
  }, [page]);

  useEffect(() => {
    if (
      inView &&
      !loading &&
      paginationData?.current_page !== paginationData?.total
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView]);

  const totalPages = useMemo(
    () =>
      Math.ceil((paginationData?.total || 0) / (paginationData?.per_page || 6)),
    [paginationData?.total, paginationData?.per_page],
  );

  const isLastPage = useMemo(() => page >= totalPages, [page, totalPages]);
  return (
    <div>
      <Heading heading='Books' route='/product/books-list/' />
      <div className='grid gap-6 md:gap-8 lg:grid-cols-2' id='book-container'>
        {bookData?.map((book) => (
          <div
            onClick={() => {
              const url = `${
                environment === 'production'
                  ? 'https://zebralearn.com'
                  : 'https://staging.zebralearn.com'
              }/books/${book.heading}-${book.softCopyId}/`;
              window.open(url, '_blank');
            }}
            key={book.hardCopyId}
            className='flex'
          >
            <BookCard key={book.hardCopyId} productData={book} />
          </div>
        ))}
        <ShouldRender check={loading}>
          {Array.from({ length: 4 }).map((_, index) => (
            <BookCardSkeleton key={index} />
          ))}
        </ShouldRender>
      </div>
      <ShouldRender check={isPagination && !isLastPage}>
        <div ref={inViewRef} className='mt-8 flex justify-center'>
          {loading && <CustomLoader />}
        </div>
      </ShouldRender>
    </div>
  );
};

export default BooksSection;
